import permissions from 'constants/permissions';
import React from 'react';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import styles from '../../index.module.scss';
import {
  ActionList,
  ActionListItem,
  ActionListItemIcon,
  AffordabilityIcon,
  BillIcon,
  Box,
  ChevronUpIcon,
  Dropdown,
  DropdownButton,
  DropdownOverlay,
  Text,
} from '@razorpay/blade/components';
import ProPlanPill from 'components/ui/ProPlanPill';
import { useHistory } from 'react-router-dom';
import { routePaths } from 'components/Routes/data';
import { useModal } from 'components/ui/Modals';
import { getStaticMediaUrl } from 'utils/Urls';
import rocketIcon from 'assets/icon--rocket.svg';
import { BulkUploadTypes } from 'constants/bulkUpload';
import useFeature from 'hooks/useFeature';

const PayrollBulkActions = ({ payrollMonth }: { payrollMonth: string }) => {
  const permission = useSelector(loggedInUserSelectors.permissions);
  const hasBulkUploadPermission = permission.includes(permissions.BULK_ADDITION_DEDUCTION);
  const isOnProPlan = useSelector(loggedInUserSelectors.isOnProPlan);
  const history = useHistory();
  const { closeModal } = useModal();
  const { isOvertimeEnabled } = useFeature('overtime');

  const onAdditionsDeductionsLopClick = () => {
    history.push(routePaths.bulkUploads.bulkAdditionDeductionLOP.root);
    closeModal();
  };

  const onOvertimeClick = () => {
    history.push({
      pathname: routePaths.bulkUploads.initiate,
      search: `type=${BulkUploadTypes.TYPE_BULK_OVERTIME_ADDITION}&payrollMonth=${payrollMonth}`,
    });
  };

  if (!hasBulkUploadPermission) return null;

  return (
    <>
      <Box height="spacing.5" backgroundColor="overlay.background.subtle" />
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        minHeight="80px"
        borderRadius="medium"
        borderWidth="thin"
        borderStyle="solid"
        borderColor="surface.border.gray.subtle">
        <Box display="flex" flexDirection="row" alignItems="center" flex="1">
          <Box
            width="spacing.8"
            height="spacing.8"
            backgroundColor="surface.background.gray.moderate"
            borderRadius="medium"
            padding="spacing.3"
            margin="spacing.4"
            display="flex"
            alignItems="center"
            justifyContent="center">
            <img src={getStaticMediaUrl(rocketIcon)} style={{ maxWidth: 'none' }} />
          </Box>
          <Box display="flex" flexDirection="column" paddingY="spacing.5" maxWidth="450px">
            {isOnProPlan ? (
              <>
                <Text size="medium" weight="semibold" color="surface.text.gray.subtle">
                  Too many employees to update?
                </Text>
                <Text size="small" color="surface.text.gray.muted">
                  Perform actions like additions and deductions in bulk!
                </Text>
              </>
            ) : (
              <>
                <Text size="medium" weight="semibold" color="surface.text.gray.subtle">
                  Introducing bulk employee additions, deductions and more!
                </Text>
                <Text size="small" color="surface.text.gray.muted">
                  Contact support to upgrade now!
                </Text>
              </>
            )}
          </Box>
        </Box>
        {isOnProPlan ? (
          <Box marginRight="spacing.5">
            <Dropdown>
              <DropdownButton
                size="small"
                variant="secondary"
                icon={ChevronUpIcon}
                iconPosition="right">
                Bulk Upload
              </DropdownButton>
              <DropdownOverlay zIndex={9999999}>
                <ActionList>
                  <ActionListItem
                    leading={<ActionListItemIcon icon={BillIcon} />}
                    title="Additions/Deductions/LOP"
                    value="additions-deductions-lop"
                    onClick={onAdditionsDeductionsLopClick}
                  />
                  {isOvertimeEnabled && (
                    <ActionListItem
                      leading={<ActionListItemIcon icon={AffordabilityIcon} />}
                      title="Overtime"
                      value="overtime"
                      onClick={onOvertimeClick}
                    />
                  )}
                </ActionList>
              </DropdownOverlay>
            </Dropdown>
          </Box>
        ) : (
          <Box marginRight="spacing.5">
            <ProPlanPill className={`${styles['bulk-upload-modal-pro-plan-pill']}`} />
          </Box>
        )}
      </Box>
    </>
  );
};

export default PayrollBulkActions;
